import * as React from 'react';
import {
  ExpressionPath,
  TypeImplementation,
  withSystem,
} from '@packages/systems/wfdl';
import {
  ERecord,
  EUnion,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
  updateExpressionAtAction,
} from '@packages/systems/ExpressionEditor-utils';
import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {FALSE, TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {
  HStack,
  IconButton,
  TextTransformSentenceIcon,
} from '@packages/systems/spring';
import {pluginName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {DataTypeSummary} from '@packages/systems/ExpressionEditor-utils-ui';

const pathToText: ExpressionPath = [{in: 'Record', at: 'text'}];
const pathToCaseSensitive: ExpressionPath = [
  {in: 'Record', at: 'caseSensitive'},
];

export const TCaseSensitiveText = TTypeConstructor(
  qualify(pluginName('Text'), typeName('CaseSensitiveText'))
);

/**
 * Text input with a case sensitivity toggle.
 * `[some text...][Aa]`
 */
export const CaseSensitiveTextType = TypeImplementation.Alias({
  type: TRecord({
    text: withSystem({isBindable: true}, TText),
    caseSensitive: withSystem({defaultValue: FALSE}, TBoolean),
  }),
  render: function CaseSensitiveTextRender({expr, dispatch}) {
    const textExpr = ContextualExpressions.getIn(expr, pathToText);
    const caseSensitiveExpr = ContextualExpressions.getIn(
      expr,
      pathToCaseSensitive
    );
    const caseSensitiveUnion = caseSensitiveExpr
      ? ContextualExpressions.getUnion(stripAnn(caseSensitiveExpr))
      : null;
    const caseSensitive = caseSensitiveUnion
      ? caseSensitiveUnion.tag === 'True'
      : false;

    const onCaseSensitivityToggle = React.useCallback(() => {
      dispatch(
        updateExpressionAtAction(
          EUnion({tag: caseSensitive ? 'False' : 'True', value: ERecord({})}),
          pathToCaseSensitive
        )
      );
    }, [caseSensitive, dispatch]);

    const textDispatch = React.useMemo(
      () => extendExpressionEditorActionAtPath(dispatch)(pathToText),
      [dispatch]
    );

    if (!textExpr || !caseSensitiveExpr) {
      return null;
    }

    return (
      <HStack spacing={1}>
        <ExpressionEditor expression={textExpr} dispatch={textDispatch} />
        <IconButton
          Icon={TextTransformSentenceIcon}
          label="Match case"
          withTooltip
          variant={caseSensitive ? 'subtle' : 'ghost'}
          isSelected={caseSensitive}
          colorVariant={caseSensitive ? 'blue' : 'default'}
          onClick={onCaseSensitivityToggle}
        />
      </HStack>
    );
  },
  renderSummary: ({expr}) => {
    const textExpr = ContextualExpressions.getIn(expr, pathToText);

    if (!textExpr) {
      return <DataTypeSummary context={expr} />;
    }

    if (ContextualExpressions.isBinding(stripAnn(textExpr))) {
      return <DataTypeSummary context={textExpr} />;
    }

    const text = ContextualExpressions.getText(stripAnn(textExpr));
    return <span>{text || ''}</span>;
  },
});
